import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { playerAPI } from '../services/api';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB' };

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.5rem 0;
`;

const SectionDesc = styled.p`
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.375rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    outline: none;
    border-color: ${BRAND.blue};
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const Hint = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: -0.25rem 0 1rem 0;
`;

const BtnPrimary = styled.button`
  padding: 0.625rem 1.5rem;
  background: ${BRAND.blue};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.25rem;
  transition: background 0.15s;

  &:hover {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function PlayerSecurityPage() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t('playerPasswordChange.toastMismatch'));
      return;
    }
    if (newPassword.length < 6) {
      toast.error(t('playerPasswordChange.toastMinLength'));
      return;
    }
    if (!currentPassword) {
      toast.error(t('playerPasswordChange.toastCurrentRequired'));
      return;
    }
    setChanging(true);
    try {
      const res = await playerAPI.changePassword(currentPassword, newPassword);
      if (res?.data?.success) {
        toast.success(t('playerPasswordChange.toastSuccess'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(res?.data?.message || t('playerPasswordChange.toastFailed'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('playerPasswordChange.toastFailed'));
    } finally {
      setChanging(false);
    }
  };

  return (
    <Card>
      <SectionTitle>{t('playerPasswordChange.sectionTitle')}</SectionTitle>
      <SectionDesc>{t('playerPasswordChange.sectionDesc')}</SectionDesc>
      <form onSubmit={handleChangePassword}>
        <div>
          <Label>{t('playerPasswordChange.currentLabel')}</Label>
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder={t('playerPasswordChange.currentPlaceholder')}
            required
            autoComplete="current-password"
          />
        </div>
        <div>
          <Label>{t('playerPasswordChange.newLabel')}</Label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('playerPasswordChange.newPlaceholder')}
            required
            minLength={6}
            autoComplete="new-password"
          />
          <Hint>{t('playerPasswordChange.hintMin')}</Hint>
        </div>
        <div>
          <Label>{t('playerPasswordChange.confirmLabel')}</Label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('playerPasswordChange.confirmPlaceholder')}
            required
            autoComplete="new-password"
          />
        </div>
        <BtnPrimary type="submit" disabled={changing}>
          {changing ? t('playerPasswordChange.submitUpdating') : t('playerPasswordChange.submitChange')}
        </BtnPrimary>
      </form>
    </Card>
  );
}
